import { type Dispatch, type ReactNode, type SetStateAction, memo, useEffect, useRef, useState } from 'react';
import { ChevronRight, FolderLock, FolderOpen, Loader2, LucideFolder, MoreVertical } from 'lucide-react';
import { useDndMonitor, useDraggable, useDroppable } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEcho } from '@laravel/echo-react';
import {
  CollapseChildrenFoldersMenuItem,
  CreateSubmissionLinkMenuItem,
  DeleteNodeMenuItem,
  DownloadZipMenuItem,
  ExpandChildrenFoldersMenuItem,
  FavoriteNodeMenuItem,
  NewAlbumMenuItem,
  NewFolderMenuItem,
  RenameNodeMenuItem,
  UploadToFolderMenuItem,
} from '@/components/menu-items/node-menu-items';
import {
  contextMenuOpenStyles,
  dropStyle,
  hoverStyles,
  selectedStyles,
  selectedTreeItemIconStyles,
  styles,
  treeItemIconStyles,
} from '@/components/sidebar/sidebar';
import { MenuProvider, MenuSeparator, contextPrimitives, dropdownPrimitives } from '@/components/menu-renderer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useBatchUpdateAssets } from '@/hooks/data/assets/useBatchUpdateAssets';
import { DAMNodeType, type TreeRecursiveChildrenNode } from '@/types/tree';
import { MoveNodeCommand } from '@/hooks/commands/folder/MoveNodeCommand';
import { DEPTH_PADDING, TreeMode } from '@/components/sidebar/tree/tree';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useMoveNodesMutation } from '@/hooks/tree/useMoveNodesMutation';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCommandContext } from '@/context/CommandContext';
import { ObjectAction, ObjectType } from '@/types/batch';
import { useTreeContext } from '@/context/TreeContext';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { DraggableItems } from '@/types/dragDrop';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type FolderProps = {
  treeBranch: TreeRecursiveChildrenNode;
  tree: Array<TreeRecursiveChildrenNode>;
  isOpen: boolean;
  setOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
  toggleNodeHandler: (id: string) => void;
  parentId?: string;
  depth: number;
  onSelectFolder?: (...args: Array<any>) => void;
  mode?: TreeMode;
  disableDrag?: boolean;
  scrollToNodeId?: string;
  onScrollToNode?: (id: string) => void;
  isSearchResult?: boolean;
};

const FolderComponent = ({
  treeBranch,
  tree,
  isOpen: isOpenProp,
  setOpenIds,
  toggleNodeHandler,
  parentId,
  depth,
  onSelectFolder,
  mode,
  disableDrag = false,
  scrollToNodeId,
  onScrollToNode,
  isSearchResult = false,
}: FolderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const itemRef = useRef<HTMLDivElement | null>(null);

  const inDialog = Boolean(mode);

  const {
    id,
    node: { name, locked = false, id: folderId },
    with_children: withChildren,
  } = treeBranch;

  const { moveNodes } = useMoveNodesMutation();
  const { selectedNodes, setSelectedNodes, includeSubFolderAssets, setUpdatedIds } = useTreeContext();
  // One of these per sidebar row, so a bare subscription re-renders every row's tooltip and menu.
  const setIsAllSelected = useBrowserStore((state) => state.setIsAllSelected);
  const isAllSelected = useBrowserStore((state) => state.isAllSelected);
  const reusableTreeSelection = useTreeStore((state) => state.reusableTreeSelection);
  const { batchUpdateAssetsMutation } = useBatchUpdateAssets();
  const { deselectAllItems } = useGallerySelection();
  const { apply } = useCommandContext();
  const { toast } = useToast();
  const tToasts = useTranslations('toasts');
  const tTree = useTranslations('tree');
  const tDialogs = useTranslations('dialogs');
  const tCommon = useTranslations('common');

  const queryClient = useQueryClient();
  const openModal = useOpenModal();
  const isOpen = isOpenProp || loading;
  const selectedNode = useTreeStore((state) => state.selectedNode);
  const isNodeSelected = selectedNode?.node.id === folderId;
  const isFolderSelected = selectedNode?.type === DAMNodeType.Folder && isNodeSelected;
  const isReusableSelection = mode === TreeMode.SelectAlbum && reusableTreeSelection?.id === folderId;
  const shouldApplySelectedStyles = (!inDialog && isNodeSelected) || isReusableSelection;
  const shouldHighlightFolderIcon = (!inDialog && isFolderSelected) || isReusableSelection;
  const formattedDragFolderId = mode ? `${mode}-${folderId}` : `sidebar-${folderId}`;
  const formattedDragId = mode ? `${mode}-${id}` : `sidebar-${id}`;

  const { setNodeRef, isOver } = useDroppable({
    id: formattedDragFolderId,
    disabled: disableDrag,
    data: {
      name: name,
      type: DraggableItems.FOLDER,
      id: folderId,
      parentId: parentId,
      accepts: [DraggableItems.FOLDER, DraggableItems.ASSET, DraggableItems.ALBUM],
    },
  });

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: formattedDragId,
    data: {
      type: DraggableItems.FOLDER,
      id,
      name,
      parentId,
      renderDragOverlay: ({ active }: { active: { id: string } }) => {
        const Icon = locked ? FolderLock : isOpen ? FolderOpen : LucideFolder;

        return (
          <div
            id={active.id}
            className={cn(
              'flex items-center rounded-md bg-highlight-base py-1 pr-2 text-text-inverted opacity-50',
              isSearchResult ? 'pl-2' : 'pl-1',
            )}
          >
            <Icon className={cn(treeItemIconStyles, selectedTreeItemIconStyles)} strokeWidth={2} />
            <div className="ml-2 flex w-full justify-between">
              <span className="line-clamp-1 text-left break-all">{name}</span>
            </div>
          </div>
        );
      },
    },
    disabled: disableDrag,
  });

  useEffect(() => {
    if (!scrollToNodeId || scrollToNodeId !== folderId || !itemRef.current) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      itemRef.current?.scrollIntoView({ block: 'nearest' });
      onScrollToNode?.(folderId);
    });

    return () => cancelAnimationFrame(frame);
  }, [scrollToNodeId, folderId, onScrollToNode, isOpen]);

  useDndMonitor({
    onDragEnd({ active, over }) {
      void (async () => {
        if (over?.id === active.id) {
          return;
        }

        if (
          over?.id === formattedDragFolderId &&
          over.data.current?.accepts?.includes(active.data.current?.type) &&
          (over.data.current?.type === DraggableItems.FOLDER ||
            active.data.current?.type === DraggableItems.ASSET ||
            active.data.current?.type === DraggableItems.ALBUM)
        ) {
          let nodeIds = [active.data?.current?.id];
          if (active.data?.current?.type === DraggableItems.ASSET && selectedNodes?.length > 1) {
            nodeIds = selectedNodes.map((asset) => asset.id);
          }

          // Check if the whole folder is selected.
          if (isAllSelected) {
            openModal(ModalId.Confirm, {
              content: tDialogs.rich('confirmMoveOrAddAllAssets', {
                type: 'folder',
                bold: (chunks) => <span className="font-bold">{chunks}</span>,
              }),
              confirmButtonVariant: 'success',
              confirmButtonLabel: tCommon('move'),
              confirmButtonDataCy: 'folder-move-all-assets-confirm',
              dismissButtonDataCy: 'folder-move-all-assets-cancel',
              onConfirm: async () => {
                try {
                  await batchUpdateAssetsMutation({
                    objectType: ObjectType.Node,
                    objectAction: ObjectAction.Move,
                    filter: {
                      ...(selectedNode?.type === DAMNodeType.Album
                        ? { albumId: active.data.current?.parentId }
                        : { folderId: active.data.current?.parentId, includeSubFolderAssets }),
                    },
                    payload: {
                      parentFolderNodeId: folderId,
                    },
                  });

                  toast({
                    title: tToasts('asset.moved.title'),
                    descriptionClassName: 'flex min-w-0 whitespace-pre',
                    description: tToasts.rich('asset.moved.description', {
                      name,
                      bold: (chunks) => <ToastHighlight chunks={chunks} />,
                    }),
                  });

                  setSelectedNodes([]);
                  setIsAllSelected(false);
                  setUpdatedIds([folderId]);
                } catch (error) {
                  errorLog('Action failed:', error);

                  toast({
                    title: tToasts('asset.moveFailed.title'),
                    variant: ToastVariant.DESTRUCTIVE,
                    descriptionClassName: 'flex min-w-0 whitespace-pre',
                    description: tToasts.rich('asset.moveFailed.description', {
                      name,
                      bold: (chunks) => <ToastHighlight chunks={chunks} />,
                    }),
                  });
                }
              },
            });

            return;
          }

          const activeType = active.data?.current?.type;
          const isAssetMove = activeType === DraggableItems.ASSET;
          const itemCount = nodeIds.length;
          const selectedCount = selectedNodes.length;
          const itemName =
            isAssetMove && itemCount === 1 ? (active.data?.current?.asset?.name ?? selectedNodes[0]?.name) : undefined;

          // Fallback logic if not all assets are selected...
          const moveNodeCommand = MoveNodeCommand(
            moveNodes,
            active.data?.current?.parentId,
            nodeIds,
            activeType,
            id,
            // Suppress global error toast when moving assets to prevent multiple toasts from appearing
            isAssetMove ? { suppressGlobalErrorToast: true } : undefined,
          );

          if (isAssetMove) {
            try {
              await apply(moveNodeCommand);
              // To be moved in the mutation, but it will require finding a way to know the node type that has been moved

              toast({
                title: tToasts('assetMove.title'),
                description: tToasts('assetMove.description', { count: selectedCount }),
              });

              setSelectedNodes([]);
              deselectAllItems();
              setIsAllSelected(false);
              setUpdatedIds([id]);
            } catch (error) {
              const status =
                typeof (error as { status?: unknown }).status === 'number'
                  ? (error as { status: number }).status
                  : undefined;
              const destinationLabel = name || tToasts('move.failed.theDestination');
              // The quotes sit outside the truncating span so a long name clips without
              // taking the closing quote with it.
              const truncatedQuoted = (label: string, trailing: string, chunks: ReactNode) => (
                <span className="inline-flex max-w-40 min-w-0 items-baseline">
                  <span className="flex-none">&quot;</span>
                  <span className="min-w-0 truncate font-bold" title={label}>
                    {chunks}
                  </span>
                  <span className="flex-none">{trailing}</span>
                </span>
              );
              const conflictDescription = (
                <p className="wrap-break-word">
                  {itemName
                    ? tToasts.rich('move.failed.conflictWithName', {
                        itemName,
                        destination: destinationLabel,
                        item: (chunks) => truncatedQuoted(itemName, '"', chunks),
                        destinationTag: (chunks) => truncatedQuoted(destinationLabel, '".\u00a0', chunks),
                      })
                    : tToasts.rich('move.failed.conflictMultiple', {
                        destination: destinationLabel,
                        destinationTag: (chunks) => truncatedQuoted(destinationLabel, '".\u00a0', chunks),
                      })}
                </p>
              );

              toast({
                title: tToasts('move.failed.title'),
                variant: ToastVariant.DESTRUCTIVE,
                description: status === 409 ? conflictDescription : tToasts('move.failed.description'),
              });
            }
          } else {
            await apply(moveNodeCommand);
            // To be moved in the mutation, but it will require finding a way to know the node type that has been moved

            toast({
              title: activeType === DraggableItems.ALBUM ? tToasts('album.moved.title') : tToasts('folder.moved.title'),
              description:
                activeType === DraggableItems.ALBUM
                  ? tToasts('album.moved.description')
                  : tToasts('folder.moved.description'),
            });

            setUpdatedIds([id]);
          }
        }
      })();
    },
  });

  useEcho(
    `folder.${isSearchResult && parentId ? parentId : folderId}.children`,
    '.folder.updated',
    (e: { folder: { id: string; parent_id: string } }) => {
      if (isSearchResult && parentId) {
        if (e.folder.id !== folderId) {
          return;
        }

        void queryClient.invalidateQueries({ queryKey: [QueryKey.Folder, folderId] });
      } else {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Folder, e.folder.id] });
      }

      // Normal tree node: react to changes in my children
      if (e.folder.parent_id) {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree, { nodeId: e.folder.parent_id }] });
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.TreeSearch] });
    },
    [folderId, parentId, isSearchResult, queryClient],
  );

  return (
    <div
      id={id}
      ref={itemRef}
      className="flex h-full flex-1 flex-col gap-y-1.5 focus-within:transition-colors focus-within:duration-150"
      title={name}
    >
      <div
        {...(!disableDrag && { ref: setNodeRef })}
        style={{
          paddingLeft: `${depth * DEPTH_PADDING + 8}px`,
        }}
        className={cn(
          'group/tree-item relative flex w-full items-center pr-2 h-full',
          styles,
          isDragging ? 'z-1 opacity-80' : 'z-0 opacity-100',
          {
            [hoverStyles]: !isDragging && !isOver,
            [contextMenuOpenStyles]: !shouldApplySelectedStyles,
            [selectedStyles]: shouldApplySelectedStyles,
          },
        )}
      >
        {withChildren && (
          <div
            onClick={() => toggleNodeHandler(id)}
            className="group/chevron flex cursor-pointer items-center px-1 select-none"
          >
            <button
              className={cn(
                'flex icon-5 items-center justify-center rounded-sm duration-150 group-hover/chevron:bg-black/10 focus-within:outline-none focus-visible:ring-2 dark:group-hover/chevron:bg-white/10',
                shouldApplySelectedStyles
                  ? 'focus-visible:ring-text-inverted'
                  : 'focus-visible:ring-highlight-hover-color',
              )}
              aria-expanded={isOpen}
              aria-label={tTree('toggleFolder', { name })}
            >
              <ChevronRight
                className={cn('p-0 text-icon-base duration-150 icon-6', {
                  'rotate-90': isOpen,
                  'text-text-inverted': shouldApplySelectedStyles,
                })}
                strokeWidth={1}
              />
            </button>
          </div>
        )}

        <div
          className={cn('flex w-full items-center', {
            [dropStyle]:
              isOver ||
              (mode === TreeMode.SelectFolder &&
                reusableTreeSelection?.type === DAMNodeType.Folder &&
                reusableTreeSelection?.id === folderId),
          })}
          {...(!disableDrag && {
            ref: setDragRef,
            ...listeners,
            ...attributes,
          })}
          tabIndex={-1}
        >
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div
                className={cn(
                  'mr-0 flex w-full flex-row items-center duration-150',
                  loading ? 'mr-7 group-hover/tree-item:mr-14' : 'group-hover/tree-item:mr-7',
                  menuOpen ? 'mr-7' : '',
                )}
              >
                <button
                  className={cn(
                    'flex w-full items-center py-0.5 pr-1 mr-0 duration-150 outline-hidden focus-visible:ring-2 pl-1 rounded-md group-has-[[aria-haspopup]:focus-visible]/tree-item:mr-8 group-hover/tree-item:group-has-[[aria-haspopup]:focus-visible]/tree-item:mr-0 focus-visible:mr-2',
                    mode === TreeMode.SelectAlbum ? 'cursor-default' : 'cursor-pointer',
                    shouldApplySelectedStyles
                      ? 'focus-visible:ring-text-inverted'
                      : 'focus-visible:ring-highlight-hover-color',
                    {
                      'ml-7': !withChildren && !isSearchResult,
                      'ml-2': isSearchResult,
                    },
                  )}
                  onClick={onSelectFolder}
                  aria-label={locked ? tTree('lockedFolder', { name }) : undefined}
                >
                  {locked ? (
                    <FolderLock
                      className={cn(
                        treeItemIconStyles,
                        shouldHighlightFolderIcon ? selectedTreeItemIconStyles : 'stroke-icon-base',
                      )}
                      strokeWidth={2}
                    />
                  ) : isOpen ? (
                    <FolderOpen
                      className={cn(
                        treeItemIconStyles,
                        shouldHighlightFolderIcon ? selectedTreeItemIconStyles : 'stroke-highlight-base',
                      )}
                      strokeWidth={2}
                    />
                  ) : (
                    <LucideFolder
                      className={cn(
                        treeItemIconStyles,
                        shouldHighlightFolderIcon ? selectedTreeItemIconStyles : 'stroke-highlight-base',
                      )}
                      strokeWidth={2}
                    />
                  )}
                  <div className="ml-2 flex w-full justify-between gap-x-2">
                    <span className="line-clamp-1 text-left text-base break-all">{name}</span>
                  </div>
                  {loading && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Loader2
                          className={cn(
                            'absolute right-4 inline-block animate-spin transition-[right] duration-150 ease-out icon-4 [animation-duration:1s] [animation-timing-function:linear] group-hover/tree-item:right-11',
                            shouldHighlightFolderIcon ? 'stroke-text-inverted' : 'stroke-icon-base',
                            { 'right-11': menuOpen },
                          )}
                          strokeWidth={2.5}
                        />
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent>{tTree('loadingChildren')}</TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  )}
                </button>
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <MenuProvider primitives={contextPrimitives}>
                {!isSearchResult && (
                  <>
                    <NewFolderMenuItem node={treeBranch} parentId={folderId} />
                    {mode !== TreeMode.SelectFolder && <NewAlbumMenuItem node={treeBranch} parentId={folderId} />}
                    <MenuSeparator />
                  </>
                )}
                <RenameNodeMenuItem node={treeBranch} />
                <DeleteNodeMenuItem node={treeBranch} danger />
                <MenuSeparator />
                {isOpen ? (
                  <CollapseChildrenFoldersMenuItem folderId={folderId} tree={tree} setOpenIds={setOpenIds} />
                ) : (
                  <ExpandChildrenFoldersMenuItem
                    folderId={folderId}
                    setLoading={setLoading}
                    disabled={!withChildren}
                    setOpenIds={setOpenIds}
                  />
                )}
                {!inDialog && (
                  <>
                    <MenuSeparator />
                    <FavoriteNodeMenuItem node={treeBranch} />
                    <MenuSeparator />
                    <UploadToFolderMenuItem folderId={folderId} />
                    <CreateSubmissionLinkMenuItem node={{ id: folderId, name }} />
                    <MenuSeparator />
                    <DownloadZipMenuItem nodeIds={[folderId]} name={name} sourceType={DAMNodeType.Folder} />
                  </>
                )}
              </MenuProvider>
            </ContextMenuContent>
          </ContextMenu>
          <DropdownMenu onOpenChange={(open) => setMenuOpen(open)}>
            <Tooltip delayDuration={1000}>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger
                  className={cn(
                    'pointer-events-none rounded focus-within:ring-2 absolute right-4 opacity-0 outline-hidden duration-150 group-hover/tree-item:pointer-events-auto group-hover/tree-item:opacity-100 focus-visible:opacity-100',
                    shouldApplySelectedStyles
                      ? 'focus-within:ring-text-inverted'
                      : 'focus-within:ring-highlight-hover-color',
                    {
                      'rotate-0 scale-100 opacity-100': menuOpen,
                    },
                  )}
                  data-cy="folder-options-trigger"
                  aria-label={tTree('folderOptions', { name })}
                >
                  <MoreVertical
                    className={cn(
                      'icon-5',
                      (!inDialog && selectedNode?.type === DAMNodeType.Folder && selectedNode.node.id === folderId) ||
                        (mode === TreeMode.SelectAlbum && reusableTreeSelection?.id === folderId)
                        ? 'stroke-text-inverted'
                        : 'stroke-icon-base',
                    )}
                    strokeWidth={2}
                  />
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>{tTree('folderOptionsTooltip')}</TooltipContent>
              </TooltipPortal>
            </Tooltip>
            <DropdownMenuContent>
              <MenuProvider primitives={dropdownPrimitives}>
                {!isSearchResult && (
                  <>
                    <NewFolderMenuItem node={treeBranch} parentId={id} />
                    {mode !== TreeMode.SelectFolder && <NewAlbumMenuItem node={treeBranch} parentId={id} />}
                    <MenuSeparator />
                  </>
                )}
                <RenameNodeMenuItem node={treeBranch} />
                <DeleteNodeMenuItem node={treeBranch} danger />
                <MenuSeparator />
                {isOpen ? (
                  <CollapseChildrenFoldersMenuItem folderId={folderId} tree={tree} setOpenIds={setOpenIds} />
                ) : (
                  <ExpandChildrenFoldersMenuItem
                    folderId={folderId}
                    setLoading={setLoading}
                    disabled={!withChildren}
                    setOpenIds={setOpenIds}
                  />
                )}
                {!inDialog && (
                  <>
                    <MenuSeparator />
                    <FavoriteNodeMenuItem node={treeBranch} />
                    <MenuSeparator />
                    <UploadToFolderMenuItem folderId={folderId} />
                    <CreateSubmissionLinkMenuItem node={{ id: folderId, name }} />
                    <MenuSeparator />
                    <DownloadZipMenuItem nodeIds={[folderId]} name={name} sourceType={DAMNodeType.Folder} />
                  </>
                )}
              </MenuProvider>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export const Folder = memo(FolderComponent);
